import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loader2, Printer, X } from "lucide-react";
import { fabricLabel, markerSizesLabel, ordinal, requiredBySize } from "@/lib/cutTickets";

const cell = "border border-gray-400 px-2 py-1";
const head = `${cell} bg-gray-300 text-left font-semibold`;
const fmt = (n) => (n || n === 0 ? Number(n).toLocaleString("it-IT") : "");

const Box = ({ title, children }) => (
  <section className="mb-5 break-inside-avoid"><h2 className="mb-1.5 border-b border-gray-400 text-xs font-bold uppercase tracking-wider">{title}</h2>{children}</section>
);

const Quantities = ({ t }) => {
  const req = requiredBySize(t.rows, t.sizes);
  return (
    <table className="w-full border-collapse text-xs">
      <thead><tr><th className={head}>Colore</th>{t.sizes.map((z) => <th key={z} className={`${head} text-center`}>{z}</th>)}<th className={`${head} text-center`}>Totale</th></tr></thead>
      <tbody>
        {t.rows.map((r) => (
          <tr key={r.batch_id || r.color}><td className={cell}><b>{r.color || "-"}</b>{(r.fabric_list || []).map((f) => <span key={f.type} className="block text-[10px] font-normal">{f.type}: {f.name} · {f.supplier || "-"}{f.color && f.color.toLowerCase() !== (r.color || "").toLowerCase() ? ` (${f.color})` : ""}</span>)}</td>{t.sizes.map((z) => <td key={z} className={`${cell} text-center`}>{r.sizes.find((x) => x.size === z)?.quantity || ""}</td>)}<td className={`${cell} text-center font-bold`}>{r.quantity}</td></tr>
        ))}
        <tr className="font-bold"><td className={cell}>Totale</td>{t.sizes.map((z) => <td key={z} className={`${cell} text-center`}>{req[z] || ""}</td>)}<td className={`${cell} text-center`}>{t.pieces}</td></tr>
      </tbody>
    </table>
  );
};

// Tipo di tessuto + nome dei tessuti scelti per i colori
const markerFabric = (m, rows, mats = []) => {
  const key = (m.fabric || "").trim().toLowerCase();
  const list = rows.flatMap((r) => r.fabric_list || []).filter((f) => (f.type || "").toLowerCase() === key || (f.name || "").toLowerCase() === key);
  const byName = mats.find((x) => x.type === "tessuto" && (x.name || "").toLowerCase() === key);
  const type = list[0]?.type || byName?.fabric_type || fabricLabel(m);
  const names = [...new Set(list.map((f) => f.name).filter(Boolean))];
  if (!names.length && byName) names.push(byName.name);
  return names.length && names[0].toLowerCase() !== type.toLowerCase() ? `${type} – ${names.join(" / ")}` : type;
};

const Markers = ({ t, mats }) => (
  <table className="w-full border-collapse text-xs">
    <thead><tr>{["N°", "Tessuto", "Taglie sul grafico", "Lunghezza"].map((h) => <th key={h} className={head}>{h}</th>)}</tr></thead>
    <tbody>
      {t.markers.map((m, i) => (
        <tr key={i} className={i % 2 ? "bg-gray-200" : "bg-white"}><td className={`${cell} font-bold`}>{i + 1}</td><td className={cell}>{markerFabric(m, t.rows, mats)}</td><td className={cell}>{markerSizesLabel(m, t.sizes)}</td>
          <td className={cell}>{m.length_m ? `${fmt(m.length_m)} m` : ""}</td></tr>
      ))}
    </tbody>
  </table>
);

export default function TaglioStampa() {
  const { id } = useParams();
  const { data: t, isLoading, isError } = useQuery({ queryKey: ["cut-ticket", id], queryFn: async () => (await api.get(`/cut-tickets/${id}`)).data });
  const { data: mats = [] } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  useEffect(() => { if (t) { document.title = `${t.sheet_name} - ${ordinal(t.number)}`; } }, [t]);
  if (isLoading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (isError || !t) return <p className="p-10 text-center text-sm">Scheda taglio non trovata.</p>;
  return (
    <div className="print-root min-h-screen bg-gray-200 py-6 text-black" data-testid="ticket-print-page">
      <div className="no-print mx-auto mb-4 flex max-w-[210mm] justify-end gap-2">
        <button onClick={() => window.close()} className="flex items-center gap-1 rounded border border-gray-400 bg-white px-3 py-1.5 text-sm" data-testid="ticket-print-close"><X className="h-4 w-4" />Chiudi</button>
        <button onClick={() => window.print()} className="flex items-center gap-1 rounded bg-black px-3 py-1.5 text-sm text-white" data-testid="ticket-print-btn-page"><Printer className="h-4 w-4" />Stampa / Salva PDF</button>
      </div>
      <div className="sheet-page">
        <header className="mb-4 flex flex-wrap items-baseline justify-between gap-x-6 border-b-2 border-black pb-2" data-testid="ticket-print-head">
          <h1 className="text-2xl font-bold uppercase">{ordinal(t.number)} · {t.sheet_name}{t.code && <span className="ml-3 font-semibold">{t.code}</span>}</h1>
          <p className="text-sm"><span className="text-gray-500">Data </span><b>{new Date(t.created_at).toLocaleDateString("it-IT")}</b></p>
        </header>
        <Box title="Quantità da tagliare per colore e taglia"><Quantities t={t} /></Box>
        <Box title="Grafici"><Markers t={t} mats={mats} /></Box>
        {t.attention?.trim() && (
          <section className="mb-5 break-inside-avoid border-[3px] border-red-600 p-3" data-testid="ticket-print-attention">
            <h2 className="mb-1 text-base font-black uppercase tracking-wider text-red-600">⚠ Attenzione</h2>
            <p className="whitespace-pre-wrap text-base font-bold">{t.attention}</p>
          </section>
        )}
        <Box title="Note per la taglieria"><p className="min-h-[60px] whitespace-pre-wrap rounded border border-gray-300 p-2 text-xs">{t.notes || " "}</p></Box>
      </div>
    </div>
  );
}
